import Store from '@/vuex';
import { service_handler, is_service_present } from '@/services';
import { is_development } from '@utilities/commons';

/**
 * Service key (API config) of the CMS pages endpoint:
 *   GET /cms/api/v1/pages/{slug}
 *
 * The actual URL comes from the remote config; the slug travels as `route_params`
 * and is resolved on the backend, consistently with the pattern already used by
 * `invadobay.related` (see blocks/invadobay/utilities.js).
 *
 * ⚠️ If the config exposes the endpoint under a different key, update it here.
 */
export const PAGE_SERVICE = 'getPageContent';

/** Ordered zones that make up a CMS page. */
export const PAGE_ZONES = ['pre_content', 'content', 'pre_footer'];

const EMPTY_PAGE = {
  site: null,
  page: { slug: null, client: null },
  pre_content: [],
  content: [],
  pre_footer: [],
  metadata: {},
};

/**
 * Normalizes the endpoint response, ensuring every zone is always an array so
 * consumers can iterate without guards.
 *
 * Zones arrive nested under `page.sections`
 * (`{ site, page: { slug, client, sections: { pre_content, content, pre_footer } } }`);
 * they are flattened to the top level of the normalized page so consumers read
 * `cms_page.pre_content` etc. without knowing about the nesting.
 *
 * @param {Object} data - Extracted payload (content of res.data.data)
 * @param {string} slug - Requested slug (used as a fallback for page.slug)
 * @returns {Object|null} Normalized page, or null if the payload is invalid
 */
export const normalize_page = (data, slug) => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const { sections, ...page_meta } = data.page || {};
  const zones = PAGE_ZONES.reduce((acc, zone) => {
    acc[zone] = Array.isArray((sections || {})[zone])
      ? sections[zone]
      : [];
    return acc;
  }, {});
  return {
    ...EMPTY_PAGE,
    site: data.site != null ? data.site : EMPTY_PAGE.site,
    page: { ...EMPTY_PAGE.page, slug, ...page_meta },
    ...zones,
  };
};

/** True if at least one zone of the page contains renderable blocks. */
export const page_has_blocks = (page) =>
  !!page && PAGE_ZONES.some((zone) => (page[zone] || []).length > 0);

/**
 * Maps a zone's raw blocks to renderable descriptors, keeping only those whose
 * `component` is mapped in `component_map`. Unmapped blocks are skipped and, when
 * provided, passed to `on_unhandled(type)` (used by the dispatcher for logging).
 *
 * @param {Array} blocks - Raw blocks (`[{ component, props }]`)
 * @param {Object} component_map - API `component` value => Vue component tag
 * @param {(type: *) => void} [on_unhandled] - called with the type of each skipped block
 * @returns {Array<{props: Object, index: number, type: *, component: *}>}
 */
export const get_renderable_blocks = (
  blocks,
  component_map = {},
  on_unhandled
) =>
  (blocks || [])
    .map((data, index) => ({
      props: (data || {}).props || {},
      index,
      type: (data || {}).component,
      component: component_map[(data || {}).component],
    }))
    .filter((block) => {
      if (!block.component) {
        if (typeof on_unhandled === 'function') {
          on_unhandled(block.type);
        }
        return false;
      }
      return true;
    });

/**
 * Extracts the page from the response body. The API wraps the payload in a
 * `data` field (body = `{ data: { site, page: { ...sections } }, metadata }`);
 * we also handle the non-wrapped format for robustness.
 *
 * @param {Object} res - Axios response
 * @returns {Object|null}
 */
const extract_page = (res) => {
  const body = (res || {}).data;
  if (!body || typeof body !== 'object') {
    return null;
  }
  return body.data || body;
};

export const get_page = (slug = 'home') => {
  
  if (!slug || !is_service_present(PAGE_SERVICE)) {
    return Promise.resolve(null);
  }
  
  try {
    const { senna_token } = Store.state || {};
    return service_handler(
      PAGE_SERVICE,
      { params: { senna_token } },
      { api_method: 'get', modal: false, route_params: { slug } }
    )
      .then((res) => normalize_page(extract_page(res), slug))
      .catch(() => null);
    
  } catch {
    return Promise.resolve(null);
  }
};

/**
 * Determines whether an href is an INTERNAL navigation destination (SPA route)
 * and, if so, returns the path to pass to `router-link` (`:to`). Returns null
 * for EXTERNAL links, so the caller uses an `<a href>`.
 *
 * Rules:
 *  - non-http schemes (mailto:, tel:, whatsapp:, …)  => external (null)
 *  - protocol-relative URLs (//host)                 => external (null)
 *  - absolute URL with a different origin            => external (null)
 *  - relative/root-relative path or same-origin       => internal (path)
 *
 * @param {string} href
 * @returns {string|null} internal path (pathname+search+hash) or null
 */
const to_internal_path = (href) => {
  try {
    if (/^#/.test(href)) {
      return null; // same-page anchor (#...) => <a href>, not navigation
    }
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(href);
    if (scheme && !['http', 'https'].includes(scheme[1].toLowerCase())) {
      return null; // mailto:, tel:, whatsapp:, ...
    }
    if (/^\/\//.test(href)) {
      return null; // protocol-relative
    }
    const current_origin =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : null;
    const url = new URL(href, current_origin || 'http://localhost');
    if (current_origin && url.origin !== current_origin) {
      return null; // different origin => external
    }
    return `${url.pathname}${url.search}${url.hash}` || '/';
  } catch {
    return null;
  }
};

/**
 * Normalizes a block `link` into a stable shape for rendering, distinguishing
 * INTERNAL links (router-link) from EXTERNAL links (`<a href>`).
 *
 * @param {(string|Object|null)} link
 * @returns {({href: string, to: (string|null), label: (string|null),
 *   internal: boolean, target: (string|null), rel: (string|null),
 *   aria_label: (string|null)}|null)}
 */
export const resolve_link = (link) => {
  if (!link) {
    return null;
  }
  const is_string = typeof link === 'string';
  const href = is_string ? link : link.url || link.href || null;
  if (!href) {
    return null;
  }
  const label = is_string
    ? null
    : link.label || link.text || link.title || null;
  const aria_label = is_string ? null : link.aria_label || null;
  // `new_tab` (boolean from the API) => opens in a new tab. A new-tab link is
  // always rendered as `<a target="_blank">` (router-link can't open new tabs).
  const new_tab = is_string
    ? false
    : !!link.new_tab || link.target === '_blank';
  // Classify internal/external only when not forced into a new tab.
  const to = new_tab ? null : to_internal_path(href);
  const internal = !!to;
  // Explicit `rel` from the payload takes precedence; otherwise default for new_tab.
  const rel = (!is_string && link.rel) || (new_tab ? 'noopener noreferrer' : null);
  return {
    href,
    to,
    label,
    internal,
    target: new_tab ? '_blank' : null,
    rel,
    aria_label,
  };
};

// Host rewrite in development only: the backend (Senna in Docker) generates
// media URLs with `host.docker.internal`, unreachable from the browser on the
// host. Same logic as VUE_APP_SENNA_LOCAL for the endpoints. In staging/prod the
// URLs are public and don't contain this host, so the rewrite is a no-op.
const DEV_MEDIA_HOST_REWRITES = { 'host.docker.internal': 'localhost' };

const rewrite_dev_media_host = (url) => {
  if (!is_development()) {
    return url;
  }
  try {
    const parsed = new URL(url);
    const target = DEV_MEDIA_HOST_REWRITES[parsed.hostname];
    if (target) {
      parsed.hostname = target;
      return parsed.toString();
    }
  } catch {
    // relative or invalid URL: leave unchanged
  }
  return url;
};

/**
 * Normalizes a block `media`/image into a stable `{ url, alt }` shape. The API
 * returns an `{ url, alt }` object (nullable), but we also handle the string
 * case for robustness.
 *
 * @param {(string|Object|null)} media
 * @returns {({url: string, alt: string}|null)}
 */
export const resolve_media = (media) => {
  if (!media) {
    return null;
  }
  const raw =
    typeof media === 'string' ? media : media.url || media.src || null;
  if (!raw) {
    return null;
  }
  return {
    url: rewrite_dev_media_host(raw),
    alt: typeof media === 'string' ? '' : media.alt || '',
  };
};

const cms_utilities = {
  PAGE_SERVICE,
  PAGE_ZONES,
  get_page,
  normalize_page,
  page_has_blocks,
  get_renderable_blocks,
  resolve_link,
  resolve_media,
};

export default cms_utilities;
